import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import { buildLeadFilterUrl } from '@/shared/lib/leadNavigation'

import SalespersonLeadsModal from './SalespersonLeadsModal'

const HIDDEN_SCROLL =
  '[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'

/** ~5 product rows visible before vertical scroll */
const PRODUCT_LIST_MAX_HEIGHT = '9.5rem'

export default function OrdersBySalesperson({
  data = [],
  period,
  customRange,
}) {
  const navigate = useNavigate()
  const list = Array.isArray(data) ? data : []
  const [selectedPerson, setSelectedPerson] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const handleCardClick = (person) => {
    setSelectedPerson({
      ...person,
      salesId: person.salesId || person._id,
    })
    setIsModalOpen(true)
  }

  return (
    <>
      <Card className="overflow-hidden rounded-2xl border border-black/[0.06] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.04)] sm:rounded-[20px]">
        <CardHeader className="border-b border-black/[0.05] px-4 py-4 sm:px-5">
          <div
            className="cursor-pointer transition hover:opacity-80"
            onClick={() =>
              navigate(
                buildLeadFilterUrl('/admin/leads', {
                  status: 'won',
                  period,
                  customRange,
                }),
              )
            }
            title="View all won leads"
          >
            <CardTitle className="text-sm font-semibold text-slate-900">
              Orders Won by Salesperson
            </CardTitle>
            <CardDescription className="text-xs text-slate-400">
              Units sold, broken down by product. Click a salesperson for full
              performance & leads breakdown.
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="px-4 py-4 sm:px-5">
          {list.length === 0 ? (
            <p className="py-8 text-center text-xs text-slate-400">
              No closed orders yet
            </p>
          ) : (
            <div
              className={`flex gap-3 overflow-x-auto pb-1 sm:gap-4 ${HIDDEN_SCROLL}`}
            >
              {list.map((person) => {
                const products = person.products ?? []
                const canScrollY = products.length > 5

                return (
                  <div
                    key={person.name}
                    onClick={() => handleCardClick(person)}
                    className="w-[min(260px,85vw)] shrink-0 cursor-pointer rounded-2xl border border-black/[0.06] bg-gradient-to-b from-slate-50 to-white p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)] transition hover:border-slate-300 hover:shadow-md"
                    title={`View performance and assigned leads for ${person.name}`}
                  >
                    <div className="mb-2">
                      <p className="truncate text-[13px] font-bold text-slate-900">
                        {person.name}
                      </p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1">
                        <span className="rounded-full bg-emerald-50 border border-emerald-200/60 px-2 py-0.5 text-[10px] font-bold tracking-wider text-emerald-700 uppercase">
                          {person.orders ?? person.won ?? 0} WON
                        </span>
                        <span className="rounded-full bg-amber-50 border border-amber-200/60 px-2 py-0.5 text-[10px] font-bold tracking-wider text-amber-700 uppercase">
                          {person.inProgress ?? 0} IN PROGRESS
                        </span>
                        <span className="rounded-full bg-red-50 border border-red-200/60 px-2 py-0.5 text-[10px] font-bold tracking-wider text-red-700 uppercase">
                          {person.lost ?? 0} LOST
                        </span>
                      </div>
                    </div>

                    <div className="mb-3 flex items-center justify-between rounded-lg bg-slate-100/70 px-2.5 py-1 text-[11px]">
                      <span className="text-slate-500">
                        Ach:{' '}
                        <strong className="font-semibold text-emerald-600">
                          ₹
                          {Number(
                            person.revenue || person.achievement || 0,
                          ).toLocaleString('en-IN')}
                        </strong>
                      </span>
                      <span className="text-slate-500">
                        Target:{' '}
                        <strong className="font-semibold text-slate-800">
                          ₹{Number(person.target || 0).toLocaleString('en-IN')}
                        </strong>
                      </span>
                    </div>

                    <div
                      className={`space-y-2 ${canScrollY ? `overflow-y-auto ${HIDDEN_SCROLL}` : ''}`}
                      style={
                        canScrollY
                          ? { maxHeight: PRODUCT_LIST_MAX_HEIGHT }
                          : undefined
                      }
                    >
                      {products.map((product, index) => (
                        <div
                          key={`${person.name}-${product.name}-${index}`}
                          className="flex items-center justify-between gap-2"
                        >
                          <span className="min-w-0 truncate text-xs text-slate-500">
                            {product.name}
                          </span>
                          <span className="shrink-0 text-xs font-semibold text-slate-900">
                            {product.qty}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <SalespersonLeadsModal
        person={selectedPerson}
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        period={period}
        customRange={customRange}
      />
    </>
  )
}
